import { SELECTORS, EVENTS } from '@bs/constants';
import { MessageBus } from '@bs/utils';
import Swiper from 'swiper/bundle';

// Space between slides, in pixels.
const SPACE_BETWEEN = 15;

// Below this width the mobile repeater is the one on screen.
const MOBILE_QUERY = '(max-width: 767.98px)';

/**
 * Collects the blocks of one repeater group within a section.
 * @param {HTMLElement} section - the section to search within
 * @param {string} selector - the block selector for the group
 * @returns {HTMLElement[]} The matching blocks
 */
const getBlocks = (section, selector) =>
  Array.from(section.querySelectorAll(selector));

/**
 * A class for the Board of Trustees module. The module renders one carousel per
 * section, so this class wraps a Swiper instance for each `__section-carousel`
 * found inside the component.
 *
 * The carousels are mobile only - the repeater is `display: none` from the
 * desktop breakpoint up - so the Swiper instances are simply left in place at
 * larger viewports rather than being destroyed.
 *
 * Separately, equalizeSectionHeights() levels the cards within each section by
 * setting an explicit height on them. The mobile and desktop repeaters both
 * exist in the DOM at all times, so their blocks are measured as two separate
 * groups (`.mobile-block` / `.desktop-block`): below 768px only the mobile
 * blocks are measured and sized, from 768px up only the desktop blocks are.
 * The inactive group is always cleared, so a tall desktop card can never leak
 * its height onto a mobile card. Runs on load, on resize and once webfonts
 * settle.
 */
export default class BoardOfTrustees extends MessageBus {
  /**
   * Constructor function of the BoardOfTrustees class. Must receive one
   * argument, a DOM node representing the module's container.
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    super();
    /**
     * DOM node that is passed into the constructor
     * @property {HTMLElement} element DOM node that is passed into the constructor
     */
    this.element = element;

    /**
     * Every Swiper instance created by this module, kept so destroy() can tear
     * them all down
     * @property {Swiper[]} swipers The Swiper instances created in enable()
     */
    this.swipers = [];

    /**
     * Pending rAF id for the debounced resize handler
     * @property {?number} resizeFrame Pending requestAnimationFrame id
     */
    this.resizeFrame = null;

    this.init();
  }

  /**
   * Initializes the view by calling the functions to create DOM references,
   * bind handlers and then create the Swiper instances.
   * @returns {BoardOfTrustees} A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();

    return this;
  }

  /**
   * Find all necessary DOM elements used in the view and cache them. There is
   * one carousel per section, so this collects all of them, plus every section
   * so its blocks can be levelled independently.
   * @returns {BoardOfTrustees} A reference to the current instance of the class
   * @chainable
   */
  cacheDomReferences() {
    this.swiperEls = Array.from(
      this.element.querySelectorAll(SELECTORS.BOARD_OF_TRUSTEES_SWIPER)
    );

    this.sections = Array.from(
      this.element.querySelectorAll(SELECTORS.BOARD_OF_TRUSTEES_SECTION)
    );

    this.mobileQuery = window.matchMedia(MOBILE_QUERY);

    return this;
  }

  /**
   * Binds the handlers this instance needs, so they can be removed in destroy().
   * @returns {BoardOfTrustees} A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    this.handleResize = this.onResize.bind(this);

    return this;
  }

  /**
   * Create a Swiper instance for every carousel in the module. Navigation
   * elements are looked up within each carousel so that instances never control
   * one another. Also levels the section blocks and keeps them level on resize.
   * @returns {BoardOfTrustees} A reference to the current instance of the class
   * @chainable
   */
  enable() {
    if (this.swiperEls.length) {
      this.swipers = this.swiperEls.map((swiperEl) => {
        const nextEl = swiperEl.querySelector(SELECTORS.BOARD_OF_TRUSTEES_NEXT);
        const prevEl = swiperEl.querySelector(SELECTORS.BOARD_OF_TRUSTEES_PREV);

        return new Swiper(swiperEl, {
          slidesPerView: 'auto',
          spaceBetween: SPACE_BETWEEN,
          navigation: {
            nextEl,
            prevEl
          }
        });
      });
    }

    this.equalizeSectionHeights();

    window.addEventListener(EVENTS.RESIZE, this.handleResize);

    // Text reflows once webfonts swap in, which changes the tallest card
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.equalizeSectionHeights());
    }

    return this;
  }

  /**
   * Re-levels the blocks on resize, throttled to one measure per frame so a
   * drag-resize doesn't thrash layout.
   * @returns {BoardOfTrustees} A reference to the current instance of the class
   * @chainable
   */
  onResize() {
    if (this.resizeFrame) window.cancelAnimationFrame(this.resizeFrame);

    this.resizeFrame = window.requestAnimationFrame(() => {
      this.resizeFrame = null;
      this.equalizeSectionHeights();
    });

    return this;
  }

  /**
   * Walks every section and gives the blocks of the currently visible repeater
   * an explicit height equal to the tallest block in that section's group.
   * Sections are handled independently, so a long bio in one section never
   * inflates the cards in another, and mobile/desktop are handled independently
   * so neither can inherit the other's height.
   * @returns {BoardOfTrustees} A reference to the current instance of the class
   * @chainable
   */
  equalizeSectionHeights() {
    const isMobile = this.mobileQuery.matches;

    this.sections.forEach((section) => {
      const mobileBlocks = getBlocks(
        section,
        SELECTORS.BOARD_OF_TRUSTEES_BLOCK_MOBILE
      );
      const desktopBlocks = getBlocks(
        section,
        SELECTORS.BOARD_OF_TRUSTEES_BLOCK_DESKTOP
      );

      const active = isMobile ? mobileBlocks : desktopBlocks;
      const inactive = isMobile ? desktopBlocks : mobileBlocks;

      // The hidden repeater never keeps a height — it would be measured from
      // the wrong breakpoint the moment it becomes visible again.
      this.clearHeights(inactive);

      this.levelBlocks(active);
    });

    return this;
  }

  /**
   * Sets every block in the group to the height of the tallest one. Heights are
   * cleared before measuring, otherwise the previously applied height is read
   * back as the natural height and the cards can only ever grow.
   * @param {HTMLElement[]} blocks - the blocks to level
   * @returns {BoardOfTrustees} A reference to the current instance of the class
   * @chainable
   */
  levelBlocks(blocks) {
    if (!blocks.length) return this;

    this.clearHeights(blocks);

    const tallest = blocks.reduce(
      (max, block) => Math.max(max, block.getBoundingClientRect().height),
      0
    );

    if (!tallest) return this;

    const height = `${tallest}px`;

    blocks.forEach((block) => block.style.setProperty('height', height));

    return this;
  }

  /**
   * Removes any inline height this module applied.
   * @param {HTMLElement[]} blocks - the blocks to reset
   * @returns {BoardOfTrustees} A reference to the current instance of the class
   * @chainable
   */
  clearHeights(blocks) {
    blocks.forEach((block) => block.style.removeProperty('height'));

    return this;
  }

  /**
   * Tears every Swiper instance down, removes the resize listener and clears
   * any heights this instance applied.
   * @returns {BoardOfTrustees} A reference to the current instance of the class
   * @chainable
   */
  destroy() {
    window.removeEventListener(EVENTS.RESIZE, this.handleResize);

    if (this.resizeFrame) {
      window.cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = null;
    }

    this.sections.forEach((section) => {
      this.clearHeights(getBlocks(section, SELECTORS.BOARD_OF_TRUSTEES_BLOCK));
    });

    this.swipers.forEach((swiper) => swiper.destroy(true, true));
    this.swipers = [];

    return this;
  }
}
